import { appStateService } from '@/state/app.state.service';
import { webStorageUtil } from '@/state/webStorage.state.service';

import {
  EEventsType,
  ELoginMethods,
  EPlatformType,
  EPublisherEvents,
  ESessionStorageKeys,
  EStorageType,
} from './enums';
import { enrichOptions, getLoginEventsData, getSessionMetadataSource } from './eventDataUtils';
import { isMixpanelInitialized, trackMixpanelEvent } from './mixpanelClient';
import { PortalBootResponse } from './types';
import { getPageNameFromPathname } from './utils';

export interface IUrlOtpPublisherTrackers {
  trackPublisherPreLoginEvent: {
    mutate: (eventData: Record<string, unknown>) => void;
  };
  trackPublisherUIEvent: {
    mutate: (eventData: Record<string, unknown>) => void;
  };
}

export interface ITrackUrlOtpPublisherParityAtAuthStartParams {
  platform?: EPlatformType;
  pathname: string;
  pages?: PortalBootResponse['pages'];
  playerId?: string;
  trackers: IUrlOtpPublisherTrackers;
}

export const isUrlOtpPageAlreadyTracked = (pathname: string): boolean =>
  webStorageUtil.get(ESessionStorageKeys.TRACKED_PAGE) === pathname;

export const markUrlOtpPageTracked = (pathname: string): void => {
  webStorageUtil.set(ESessionStorageKeys.TRACKED_PAGE, pathname, EStorageType.SESSION_STORAGE);
};

export const trackUrlOtpPublisherParityAtAuthStart = ({
  platform,
  pathname,
  pages,
  playerId,
  trackers,
}: ITrackUrlOtpPublisherParityAtAuthStartParams): void => {
  const loginMethod = ELoginMethods.OTP;
  const sessionMetadataSource = getSessionMetadataSource(pathname);
  const playerSession = { sessionMetadata: { source: sessionMetadataSource } };

  trackMixpanelEvent(
    EEventsType.PORTAL_LOGIN_DISPLAYED,
    getLoginEventsData({ platform, method: loginMethod }),
  );
  trackers.trackPublisherPreLoginEvent.mutate({
    ...enrichOptions({
      platform,
      eventName: EPublisherEvents.PORTAL_LOGIN_DISPLAYED,
      method: loginMethod,
      playerSession,
    }),
  });

  trackMixpanelEvent(
    EEventsType.PORTAL_LOGIN_METHOD_SELECTED,
    getLoginEventsData({ platform, method: loginMethod }),
  );
  trackers.trackPublisherPreLoginEvent.mutate({
    ...enrichOptions({
      platform,
      eventName: EPublisherEvents.PORTAL_LOGIN_METHOD_SELECTED,
      method: loginMethod,
      playerSession,
    }),
  });

  if (isUrlOtpPageAlreadyTracked(pathname)) {
    return;
  }

  markUrlOtpPageTracked(pathname);

  const resolvedPages = pages || appStateService.initialBoot.get()?.pages;
  const pageName = getPageNameFromPathname(pathname, resolvedPages);

  if (isMixpanelInitialized()) {
    trackMixpanelEvent(EEventsType.PORTAL_PAGE_DISPLAYED, {
      sessionMetaData_pageUrl: pathname,
      sessionMetaData_pageName: pageName,
      device_platform: platform,
      player_id: playerId,
      appVersion: process.env.NEXT_PUBLIC_APP_VERSION,
    });
  }

  trackers.trackPublisherUIEvent.mutate({
    ...enrichOptions({
      platform,
      eventName: EPublisherEvents.PORTAL_PAGE_DISPLAYED,
      page: [{ pageUrl: pathname, pageName }],
      playerId,
    }),
  });
};
